/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import React, { useContext, useEffect, useState } from 'react';
import DebouncedTextField from '../../../customs/debouncedTextField/DebouncedTextField';
import { useCSS } from '../../../../provider/CSSProvider';
import style from './AddOxygenDialog.scss?inline';
import dayjs, { Dayjs } from 'dayjs';
import { CheckCircleOutline } from '@mui/icons-material';
import { defaultRecords } from '../../../../DefaultRecords';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import { NodeType } from '../../../../backendModels/report.model';
import { Tooltip } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { TimePicker } from '../../../customs/timePicker/TimePicker';
import { hhmmToCorrectedDateJS } from '../../../../utils/util';

export interface AddOxygenDialogResponse {
  timestamp: Dayjs;
  oxygenFlowValue: number;
}

interface AddOxygenDialogProps {
  isDialogOpen: boolean;
  onOpenChanged: (open: boolean) => void;
  onConfirm: (oxygen: AddOxygenDialogResponse) => void;
}

export function AddOxygenDialog(props: AddOxygenDialogProps) {
  useCSS(style);

  const reportAPI = useContext(ReportsAPIContext);
  const nodes = reportAPI.findNodeWithRecord(NodeType.GENERIC, defaultRecords.oxygen.type);

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [oxygenValue, setOxygenValue] = useState<number | undefined>(undefined);
  const onOxygenValueChanged = (value: string) => {
    setOxygenValue(value !== '' ? Number(value) : undefined);
  };

  const [timestamp, setTimestamp] = useState(dayjs().format('HH:mm'));

  function checkForNoDuplicate() {
    if (timestamp === '') return false;
    const [hour, minute] = timestamp.split(':').map((part) => Number(part));
    const duplicates = nodes.filter(
      (node) => dayjs(node.effectiveTimestamp).minute() === minute && dayjs(node.effectiveTimestamp).hour() === hour
    );
    return duplicates.length === 0;
  }

  function handleClose() {
    props.onOpenChanged(false);
    setTimeout(() => {
      setOxygenValue(undefined);
      setTimestamp('');
      setIsConfirmed(false);
    }, 200);
  }

  function handleAdd() {
    if (!isAddable()) {
      return;
    }

    props.onConfirm({ timestamp: hhmmToCorrectedDateJS(timestamp), oxygenFlowValue: oxygenValue! });

    setIsConfirmed(true);
    setTimeout(handleClose, 1000);
  }

  function isAddable() {
    if (checkForNoDuplicate()) {
      return oxygenValue !== undefined && timestamp !== '';
    }
    return false;
  }

  useEffect(() => {
    if (props.isDialogOpen) {
      setTimestamp(dayjs().format('HH:mm'));
    }
  }, [props.isDialogOpen]);

  return (
    <Dialog open={props.isDialogOpen} className='add-oxygen-dialog' closeAfterTransition={false}>
      <DialogTitle>Sauerstoff hinzufügen</DialogTitle>
      <DialogContent>
        {isConfirmed ? (
          <div className='add-oxygen-dialog-confirmed'>
            <CheckCircleOutline className='check-circle' />
          </div>
        ) : (
          <div>
            <TimePicker required={true} value={timestamp} onChange={setTimestamp} />
            <DebouncedTextField
              fullWidth
              type='number'
              label='Sauerstoffgabe [l/m]'
              variant='outlined'
              value={oxygenValue?.toString() ?? ''}
              onDebounceChange={onOxygenValueChanged}
              debounceTime={10}
              inputProps={{ step: 0.1, min: 0.1 }}
            />
          </div>
        )}
      </DialogContent>
      {!isConfirmed && (
        <DialogActions>
          {!isAddable() && oxygenValue && timestamp !== '' && (
            <Tooltip
              arrow
              title='Für den gewünschten Zeitpunkt ist bereits ein Eintrag vorhanden. Bitte passen Sie den Zeitpunkt an oder löschen Sie den bereits vorhanden Eintrag.'>
              <WarningIcon color='warning' />
            </Tooltip>
          )}
          {timestamp === '' && (
            <Tooltip arrow title='Bitte geben Sie einen Zeitpunkt an.'>
              <WarningIcon color='warning' />
            </Tooltip>
          )}
          <Button className='cancel-button' variant='outlined' onClick={handleClose}>
            Abbrechen
          </Button>
          <Button onClick={handleAdd} autoFocus disabled={!isAddable()} variant='contained'>
            Hinzufügen
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
